import { tr, usePartnerTranslation } from "../../lib/language";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Plus, Archive, ReceiptText, Store, Info, Star, Loader2, ShieldCheck, ArrowRight, Images, Pencil } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { useAuth } from "../../lib/auth";
import { useLegalAccount } from "../../lib/legal";
import { formatMoney, normalizeCurrency } from "../../lib/money";
import { publicShopUrl } from "../../lib/shopUrls";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SHOP_HANDLE = "trip-city"; // current supplier (mocked in /api/shops/me)

/* ------------ One KPI cell ------------ */
const KpiCell = ({
  label,
  info,
  children,
  testid
}) => {
  usePartnerTranslation();
  return <div data-testid={testid} className="flex-1">
    <div className="flex items-center gap-1.5">
      <span className="text-sm text-[#6B7280]">{label}</span>
      {info && <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3.5 w-3.5 cursor-help text-[#9BA1AB]" strokeWidth={1.8} tabIndex={0} />
            </TooltipTrigger>
            <TooltipContent className="max-w-[260px] border-[#EAECEF] bg-[#0F1115] text-white" sideOffset={6}>
              <p className="text-xs">{info}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>}
    </div>
    <div className="mt-2 text-2xl font-extrabold tracking-tight text-[#0F1115] sm:text-[28px]">
      {children}
    </div>
  </div>;
};

/* ------------ Quick action card ------------ */
const ActionCard = ({
  icon: Icon,
  label,
  onClick,
  testid
}) => {
  usePartnerTranslation();
  return <button type="button" onClick={onClick} data-testid={testid} className="group flex aspect-[1.45/1] flex-col items-center justify-center gap-2 rounded-lg border border-[#E6E8EC] bg-white px-2.5 text-center transition-all hover:-translate-y-0.5 hover:border-[#0F1115] hover:shadow-[0_8px_24px_-12px_rgba(15,17,21,0.18)] sm:aspect-[4/3] sm:gap-3 sm:rounded-xl sm:px-4">
    <Icon className="h-6 w-6 text-[#0F1115] transition-colors group-hover:text-[#FF5757] sm:h-10 sm:w-10" strokeWidth={1.4} />
    <span className="text-xs font-semibold leading-tight text-[#0F1115] sm:text-sm">{label}</span>
  </button>;
};

/* ------------ Page ------------ */
export default function HomePage() {
  usePartnerTranslation();
  const navigate = useNavigate();
  const { role } = useAuth();
  const showRevenue = role !== "operations";
  const showLegalBanner = role === "administration" || !role;
  const { legal, loaded: legalLoaded } = useLegalAccount();
  const [data, setData] = useState(null);
  const [customerRating, setCustomerRating] = useState(null);
  const [shopHandle, setShopHandle] = useState("");
  const [financeSummary, setFinanceSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [perfRes, meRes, financeRes, ratingRes] = await Promise.all([
          axios.get(`${API}/dashboard/me/booking-count`).catch(() => ({ data: null })),
          axios.get(`${API}/shops/me`),
          showRevenue
            ? axios.get(`${API}/finance/summary`).catch(() => ({ data: null }))
            : Promise.resolve({ data: null }),
          axios.get(`${API}/dashboard/me/customer-rating`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setData({ bookings: perfRes.data });
        setCustomerRating(ratingRes.data);
        setShopHandle(meRes.data?.handle || "");
        setFinanceSummary(financeRes.data);
      } catch (err) {
        if (!cancelled && process.env.NODE_ENV !== "production") {
          console.warn("[home] dashboard load failed", err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showRevenue]);

  const visitShop = () => {
    if (!shopHandle) return;
    window.open(publicShopUrl(shopHandle), "_blank", "noopener,noreferrer");
  };

  const revenueCurrency = normalizeCurrency(financeSummary?.currency);

  return (
    <main
      data-testid="home-page"
      className="mx-auto w-full max-w-[1100px] flex-1 px-4 py-10 sm:px-6 md:px-10"
    >
      {/* Legal onboarding banner — shown to admins until Legal is filled */}
      {showLegalBanner && legalLoaded && legal && !legal.exists && (
        <section
          data-testid="home-legal-banner"
          className="mb-6 overflow-hidden rounded-2xl border border-[#FFD7D7] bg-gradient-to-br from-[#FFF7F7] to-white"
        >
          <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#FF5757] text-white">
                <ShieldCheck className="h-5 w-5" strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-extrabold tracking-tight text-[#0F1115] sm:text-lg">{tr("Complete your Legal info to start selling")}</h3>
                <p className="mt-1 text-sm text-[#6B7280]">{tr("We need a few details before you can publish products on Voocly. In the meantime, you can create products and save them in drafts. Publishing unlocks once your Legal section is complete.")}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate("/account/legal")}
              data-testid="home-legal-cta"
              className="humrahe-btn-primary inline-flex h-10 flex-shrink-0 items-center gap-1.5 rounded-md px-5 text-sm font-semibold"
            >{tr("Fill Legal info")}<ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </section>
      )}

      {/* Business performance card */}
      <section
        data-testid="business-performance"
        className="rounded-2xl border border-[#E6E8EC] bg-white p-5 sm:p-7"
      >
        <h2 className="text-lg font-extrabold tracking-tight text-[#0F1115] sm:text-xl">{tr("Business performance")}</h2>
        <p className="mt-1 text-sm text-[#6B7280]">
          {showRevenue
            ? tr("Estimated revenue is for this month. Customer ratings cover the last 30 days.")
            : tr("Customer ratings cover the last 30 days.")}
        </p>

        {loading ? (
          <div className="mt-6 flex items-center gap-2 text-sm text-[#6B7280]">
            <Loader2 className="h-4 w-4 animate-spin" />{tr("Loading metrics…")}</div>
        ) : (
          <div className="mt-6 flex flex-col gap-6 sm:flex-row sm:gap-10">
            {showRevenue && (
            <KpiCell
              testid="kpi-revenue"
              label={tr("Estimated revenue")}
              info={tr("Estimated net payout from this month’s confirmed, unpaid bookings, matching your Finance summary.")}
            >
              {formatMoney(financeSummary?.pending_balance, revenueCurrency)}
            </KpiCell>
            )}
            <KpiCell
              testid="kpi-rating"
              label={tr("Average customer rating")}
              info={customerRating?.info || tr("Average customer review rating out of 5, based on reviews submitted in the last 30 days.")}
            >
              <span className="inline-flex items-center gap-1.5">
                {customerRating?.value != null ? Number(customerRating.value).toFixed(2) : "—"}
                <Star
                  className="h-5 w-5 text-[#FF5757]"
                  fill="#FF5757"
                  strokeWidth={0}
                />
              </span>
              {customerRating?.review_count === 0 && (
                <p className="mt-1 text-xs font-normal tracking-normal text-[#6B7280]">{tr("No reviews in the last 30 days")}</p>
              )}
            </KpiCell>
            <KpiCell
              testid="kpi-bookings"
              label={tr("Bookings")}
              info={tr("Confirmed, unpaid bookings for activities this month, matching estimated revenue.")}
            >
              {(financeSummary?.confirmed_booking_count ?? data?.bookings?.value ?? 0).toLocaleString()}
            </KpiCell>
          </div>
        )}
      </section>

      {/* Quick actions — 3+3 grid:
            row 1 = product/booking actions
            row 2 = shop actions (visit, gallery upload, edit details) */}
      <section
        data-testid="home-actions"
        className="mt-5 grid grid-cols-2 gap-2.5 sm:mt-6 sm:grid-cols-3 sm:gap-4"
      >
        <ActionCard
          icon={Plus}
          label={tr("Create a product")}
          onClick={() => navigate("/create")}
          testid="action-create-product"
        />
        <ActionCard
          icon={Archive}
          label={tr("Manage products")}
          onClick={() => navigate("/products")}
          testid="action-manage-products"
        />
        <ActionCard
          icon={ReceiptText}
          label={tr("View bookings")}
          onClick={() => navigate("/bookings")}
          testid="action-view-bookings"
        />
        <ActionCard
          icon={Store}
          label={tr("Visit shop")}
          onClick={visitShop}
          testid="action-visit-shop"
        />
        <ActionCard
          icon={Images}
          label={tr("Upload in gallery")}
          onClick={() => navigate("/my-shop/gallery")}
          testid="action-upload-gallery"
        />
        <ActionCard
          icon={Pencil}
          label={tr("Edit shop details")}
          onClick={() => navigate("/my-shop/edit")}
          testid="action-edit-shop"
        />
      </section>
    </main>
  );
}
